/**
 * Persist draft cooling system rows created before the parent request is saved (#996 Phase 2).
 * Mirrors smallUpsConfigurationDraft.
 */

import { api } from './api';
import { toCreateCoolingSystemPayload } from './coolingSystemPayload';
import { networkService } from './network';
import { offlineCache } from './offlineCache';
import {
  stableDraftInstanceId,
  withDraftDimensionUnitTempId,
} from '../request/dimensionUnitChoices';

import type { CoolingSystemConfiguration } from '../types/coolingSystem';

export async function persistDraftCoolingSystemConfigurations(
  requestId: string | number,
  units: CoolingSystemConfiguration[],
): Promise<void> {
  const createdIds: number[] = [];
  const idKey = String(requestId);

  try {
    for (const unit of units) {
      const created = await api.post<CoolingSystemConfiguration>(
        `/requests/${requestId}/cooling-systems`,
        withDraftDimensionUnitTempId(
          unit.id,
          toCreateCoolingSystemPayload(unit),
        ),
      );
      if (created.id) {
        createdIds.push(created.id);
      }
    }
  } catch (error) {
    for (const coolingSystemId of createdIds) {
      try {
        await api.delete(
          `/requests/${idKey}/cooling-systems/${coolingSystemId}`,
        );
      } catch (rollbackError) {
        console.error(
          'Failed to roll back cooling system after partial draft flush:',
          rollbackError,
        );
      }
    }
    throw error;
  }
}

/**
 * Flush draft cooling systems after Create Request save — online POST or offline cache + queue.
 */
export async function flushDraftCoolingSystemConfigurations(
  requestId: string | number,
  units: CoolingSystemConfiguration[],
  isOfflineRequest = false,
): Promise<void> {
  if (!units.length) {
    return;
  }

  const id = String(requestId);

  if (isOfflineRequest || !networkService.isConnected()) {
    const normalized = units.map((unit, index) => ({
      ...toCreateCoolingSystemPayload(unit),
      id: stableDraftInstanceId(unit.id, index),
      requestId: typeof requestId === 'number' ? requestId : 0,
      displayOrder: unit.displayOrder ?? index + 1,
      status: unit.status ?? 'new',
    })) as CoolingSystemConfiguration[];

    await offlineCache.updateCachedRequestCoolingSystems(id, normalized);
    for (const unit of normalized) {
      await offlineCache.addCoolingSystemToOfflineQueue(
        id,
        'create',
        withDraftDimensionUnitTempId(
          unit.id,
          toCreateCoolingSystemPayload(unit),
        ),
        unit.id,
      );
    }
    return;
  }

  await persistDraftCoolingSystemConfigurations(requestId, units);
}
